import { z } from "zod";

export const BETA_ALLOWLIST_STATUSES = ["INVITED", "ACTIVE", "BLOCKED"] as const;
export type BetaAllowlistStatus = (typeof BETA_ALLOWLIST_STATUSES)[number];

export const BETA_ACCESS_TYPES = ["BETA", "ADMIN"] as const;
export type BetaAccessType = (typeof BETA_ACCESS_TYPES)[number];

export const BETA_ACCESS_REQUEST_STATUSES = ["NEW", "REVIEWED", "APPROVED", "REJECTED"] as const;
export type BetaAccessRequestStatus = (typeof BETA_ACCESS_REQUEST_STATUSES)[number];

export const betaAllowlistStatusSchema = z.enum(BETA_ALLOWLIST_STATUSES);
export const betaAccessTypeSchema = z.enum(BETA_ACCESS_TYPES);
export const betaAccessRequestStatusSchema = z.enum(BETA_ACCESS_REQUEST_STATUSES);

/** Lowercase trimmed email — use for all allowlist and request writes/lookups. */
export function normalizeBetaEmail(email: string): string {
  return email.trim().toLowerCase();
}

export const betaAllowlistEntrySchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  status: betaAllowlistStatusSchema,
  accessType: betaAccessTypeSchema,
  companyName: z.string().nullable(),
  invitedBy: z.string().uuid().nullable(),
  usedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type BetaAllowlistEntry = z.infer<typeof betaAllowlistEntrySchema>;

export const betaAccessRequestSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  email: z.string().email(),
  companyName: z.string(),
  companySize: z.string(),
  message: z.string().nullable(),
  status: betaAccessRequestStatusSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type BetaAccessRequest = z.infer<typeof betaAccessRequestSchema>;

export const COMPANY_SIZE_OPTIONS = [
  "1–10 workers",
  "11–50 workers",
  "51–200 workers",
  "201–500 workers",
  "500+ workers",
] as const;

export type CompanySizeOption = (typeof COMPANY_SIZE_OPTIONS)[number];

export const companySizeSchema = z.enum(COMPANY_SIZE_OPTIONS);

/** Strip control characters and collapse whitespace for user-submitted text. */
export function sanitizeAccessRequestText(value: string, maxLength: number): string {
  return value
    // eslint-disable-next-line no-control-regex -- intentional strip of ASCII control chars
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, maxLength);
}

export const createBetaAccessRequestSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .max(120)
    .transform((v) => sanitizeAccessRequestText(v, 120)),
  email: z
    .string()
    .trim()
    .email("Enter a valid work email")
    .max(320)
    .transform((v) => normalizeBetaEmail(v)),
  companyName: z
    .string()
    .trim()
    .min(1, "Company name is required")
    .max(120)
    .transform((v) => sanitizeAccessRequestText(v, 120)),
  companySize: companySizeSchema,
  message: z
    .string()
    .trim()
    .max(2000)
    .optional()
    .transform((v) => (v ? sanitizeAccessRequestText(v, 2000) : undefined)),
});

export type CreateBetaAccessRequestInput = z.infer<typeof createBetaAccessRequestSchema>;

/** Statuses that permit self-serve BETA workspace creation. */
export const BETA_ALLOWLIST_ACTIVE_STATUSES: readonly BetaAllowlistStatus[] = [
  "INVITED",
  "ACTIVE",
];

export interface AccessGateState {
  isPlatformAdmin: boolean;
  platformRole: "PLATFORM_ADMIN" | null;
  canAccessWorkerDocuments: boolean;
}

export const accessGateStateSchema = z.object({
  isPlatformAdmin: z.boolean(),
  platformRole: z.enum(["PLATFORM_ADMIN"]).nullable(),
  canAccessWorkerDocuments: z.boolean(),
});

export const POST_LOGIN_PATHS = {
  REQUEST_ACCESS: "/request-access",
  NOT_INVITED: "/not-invited",
  ACCESS_DENIED: "/access-denied",
  BETA_ONBOARDING: "/onboarding/beta",
  DASHBOARD: "/dashboard",
  ADMIN: "/admin",
} as const;

export type PostLoginPath = (typeof POST_LOGIN_PATHS)[keyof typeof POST_LOGIN_PATHS];

export function canCreateBetaWorkspaceFromAllowlist(entry: {
  status: BetaAllowlistStatus;
  accessType: BetaAccessType;
}): boolean {
  return (
    entry.accessType === "BETA" &&
    BETA_ALLOWLIST_ACTIVE_STATUSES.includes(entry.status)
  );
}
